import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';

// ── Types ─────────────────────────────────────────────────────────────────────
type OrbState = 'idle' | 'capturing' | 'acting' | 'pending_approval' | 'copilot' | 'killed';

interface OrbConfig {
  sessionToken: string | null;
  agentConnected: boolean;
}

interface HwStatus {
  tier: string;         // 'nano' | 'nano-plus' | 'small' | 'medium' | 'large'
  model: string;        // display name e.g. "Gemma 4 E4B (Small — ~2.8 GB, multimodal)"
  model_key: string;
  backend: 'CPU' | 'GPU';
  ram_gb: number | null;
  vram_gb: number | null;
  sovereign: boolean;
}

// ── Orb visual configuration per state ───────────────────────────────────────
const ORB_VISUALS: Record<OrbState, { color: string; glow: string; label: string; pulse: boolean }> = {
  idle:             { color: '#6366f1', glow: 'rgba(99,102,241,0.4)',   label: 'Replica · Aware',        pulse: true  },
  capturing:        { color: '#3b82f6', glow: 'rgba(59,130,246,0.5)',   label: 'Replica · Watching',     pulse: false },
  acting:           { color: '#8b5cf6', glow: 'rgba(139,92,246,0.6)',   label: 'Replica · Acting',       pulse: false },
  pending_approval: { color: '#f59e0b', glow: 'rgba(245,158,11,0.6)',   label: 'Awaiting Approval',      pulse: true  },
  copilot:          { color: '#10b981', glow: 'rgba(16,185,129,0.5)',   label: 'Co-Pilot · Guiding',     pulse: false },
  killed:           { color: '#ef4444', glow: 'rgba(239,68,68,0.5)',    label: 'Session Ended',          pulse: false },
};

// ── Reusable inline styles (keeps JSX clean) ─────────────────────────────────
const pillBg = 'rgba(10,12,20,0.92)';

// ── Component ─────────────────────────────────────────────────────────────────
const SovereignOrb: React.FC = () => {
  const { user } = useAuth();
  const { userReplicas } = useReplica();

  const [orbState, setOrbState] = useState<OrbState>('idle');
  const [config, setConfig] = useState<OrbConfig>({ sessionToken: null, agentConnected: false });
  const [hwStatus, setHwStatus] = useState<HwStatus | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [recentActions, setRecentActions] = useState<{ action: string; ts: string; ok: boolean }[]>([]);
  const [position, setPosition] = useState({ x: window.innerWidth - 100, y: window.innerHeight - 130 });
  const [dragging, setDragging] = useState(false);
  const [orbDesktopOn, setOrbDesktopOn] = useState(true); // desktop orb visible

  // ── Connect Agent panel state ─────────────────────────────────────────────
  const [showConnectPanel, setShowConnectPanel] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [generatedCmd, setGeneratedCmd] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const dragOffset = useRef({ x: 0, y: 0 });
  const wsRef = useRef<WebSocket | null>(null);
  const animFrameRef = useRef<number>(0);

  const visual = ORB_VISUALS[orbState];

  // ── WebSocket connection to frontend relay ────────────────────────────────
  const connectWs = useCallback((token: string) => {
    if (wsRef.current) wsRef.current.close();
    const wsUrl = `${import.meta.env.VITE_API_URL?.replace('https', 'wss') ?? 'ws://localhost:3001'}/ws/fcc-frontend/${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'agent.connected') {
          setConfig(c => ({ ...c, agentConnected: true }));
          setOrbState('idle');
          setOrbDesktopOn(true); // auto-show desktop orb on connect
          setShowConnectPanel(false); // dismiss connect panel
        }
        if (msg.type === 'agent.disconnected') {
          setConfig(c => ({ ...c, agentConnected: false }));
          setOrbState('idle');
        }
        if (msg.type === 'orb.state') {
          setOrbState(msg.state as OrbState);
        }
        if (msg.type === 'hw_status') {
          setHwStatus(msg as HwStatus);
        }
        if (msg.type === 'orb.desktop_hidden') {
          // User double-clicked desktop orb to hide — sync toggle in browser
          setOrbDesktopOn(false);
        }
        if (msg.type === 'tool.result') {
          const { action, result } = msg;
          setRecentActions(prev => [
            { action: action ?? 'action', ts: new Date().toLocaleTimeString(), ok: result?.ok ?? true },
            ...prev.slice(0, 4),
          ]);
          setOrbState('idle');
        }
      } catch {}
    };

    ws.onclose = () => setConfig(c => ({ ...c, agentConnected: false }));
  }, []);

  // ── Send message to agent via WebSocket ──────────────────────────────────
  const sendWs = useCallback((msg: object) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    }
  }, []);

  // ── Toggle desktop orb ────────────────────────────────────────────────────
  const toggleDesktopOrb = useCallback(() => {
    const next = !orbDesktopOn;
    setOrbDesktopOn(next);
    sendWs({ type: 'overlay.orb_toggle', show: next });
  }, [orbDesktopOn, sendWs]);

  // ── Kill switch ───────────────────────────────────────────────────────────
  const killSession = useCallback(async () => {
    if (!config.sessionToken) return;
    await fetch(`/api/fcc/session/${config.sessionToken}`, { method: 'DELETE' });
    wsRef.current?.send(JSON.stringify({ type: 'session.kill' }));
    setOrbState('killed');
    setConfig({ sessionToken: null, agentConnected: false });
    setGeneratedCmd(null);
  }, [config.sessionToken]);

  // ── OS detection ──────────────────────────────────────────────────────────
  const userPlatform = navigator.platform.toLowerCase().includes('mac') ? 'macos'
    : navigator.platform.toLowerCase().includes('win') ? 'windows' : 'other';

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const AGENT_DOWNLOAD_URLS: Record<string, string> = {
    windows: `${API_BASE}/api/v1/agent/download/windows`,
    macos: `${API_BASE}/api/v1/agent/download/macos`,
    other: `${API_BASE}/api/v1/agent/download`,
  };

  // ── Connect Agent: create session + generate .hrconnect ───────────────────
  const handleConnectAgent = useCallback(async () => {
    if (!user) return;
    setConnectLoading(true);
    setConnectError(null);

    // Grab the first replica or use a placeholder
    const replicaId = userReplicas.length > 0 ? userReplicas[0].id : 'default';

    const resp = await api.post<{
      sessionToken: string;
      hmacSecret: string;
      wsUrl: string;
      expiresAt: string;
    }>('/fcc/session', { replicaId });

    setConnectLoading(false);

    if (resp.error || !resp.data) {
      setConnectError(resp.error ?? 'Failed to create session');
      return;
    }

    const { sessionToken, hmacSecret } = resp.data;
    setConfig(c => ({ ...c, sessionToken }));

    // Build WebSocket endpoint
    const wsBase = import.meta.env.VITE_API_URL?.replace('https', 'wss')?.replace('http', 'ws') ?? 'ws://localhost:3001';
    const wsEndpoint = `${wsBase}/ws/fcc/`;

    // Store for .hrconnect download and deep link
    setGeneratedCmd(JSON.stringify({ sessionToken, hmacSecret, wsEndpoint }));

    // Auto-connect the frontend WS so it's ready when the agent connects
    connectWs(sessionToken);
  }, [user, userReplicas, connectWs]);

  // ── Download .hrconnect file ──────────────────────────────────────────────
  const downloadHrConnect = useCallback(() => {
    if (!generatedCmd) return;
    try {
      const data = JSON.parse(generatedCmd);
      const blob = new Blob(
        [JSON.stringify({
          sessionToken: data.sessionToken,
          hmacSecret: data.hmacSecret,
          wsEndpoint: data.wsEndpoint,
        }, null, 2)],
        { type: 'application/json' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'humanreplica.hrconnect';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }, [generatedCmd]);

  // ── Launch via deep link (if agent already installed) ─────────────────────
  const launchDeepLink = useCallback(() => {
    if (!generatedCmd) return;
    try {
      const data = JSON.parse(generatedCmd);
      const deepLink = `humanreplica://connect?token=${data.sessionToken}&secret=${data.hmacSecret}&ws=${encodeURIComponent(data.wsEndpoint)}`;
      window.location.href = deepLink;
    } catch {}
  }, [generatedCmd]);

  // ── Dragging logic ────────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.orb-tray')) return;
    setDragging(true);
    dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging) return;
      setPosition({
        x: Math.max(40, Math.min(window.innerWidth - 60, e.clientX - dragOffset.current.x)),
        y: Math.max(40, Math.min(window.innerHeight - 80, e.clientY - dragOffset.current.y)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  // Keep orb in bounds on window resize
  useEffect(() => {
    const onResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - 60),
        y: Math.min(prev.y, window.innerHeight - 80),
      }));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Cleanup WS on unmount ─────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // ── Pulse animation via CSS var ───────────────────────────────────────────
  const orbSize = 52;
  const halfOrb = orbSize / 2;

  return (
    <>
      {/* ── Global orb styles ──────────────────────────────────────────── */}
      <style>{`
        @keyframes orbPulse {
          0%, 100% { box-shadow: 0 0 0 0 var(--orb-glow), 0 0 22px 6px var(--orb-glow); }
          50%       { box-shadow: 0 0 0 12px transparent, 0 0 30px 10px var(--orb-glow); }
        }
        @keyframes orbSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .sovereign-orb {
          cursor: grab;
          user-select: none;
          transition: background 0.4s ease, box-shadow 0.4s ease;
        }
        .sovereign-orb:active { cursor: grabbing; }
        .orb-pulse { animation: orbPulse 2s ease-in-out infinite; }
        .orb-tray {
          animation: fadeInUp 0.2s ease-out;
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }
        .action-row { transition: background 0.15s; }
        .action-row:hover { background: rgba(255,255,255,0.06); }
        .connect-agent-btn {
          transition: all 0.2s ease;
        }
        .connect-agent-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(99,102,241,0.4) !important;
        }
        .connect-agent-btn:active {
          transform: translateY(0);
        }
        .copy-btn:hover { background: rgba(99,102,241,0.3) !important; }
      `}</style>

      {/* ── OPTION A: Desktop Mode bar (agent connected) ──────────────── */}
      {config.agentConnected ? (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 20,
            zIndex: 99999,
            fontFamily: 'Inter, system-ui, sans-serif',
            pointerEvents: 'auto',
          }}
        >
          {/* Compact status pill */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(10,12,20,0.90)',
              border: `1px solid ${visual.color}55`,
              borderRadius: 40,
              padding: '6px 12px 6px 8px',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              boxShadow: `0 0 16px 2px ${visual.glow}`,
              cursor: 'default',
            }}
          >
            {/* State dot */}
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: visual.color,
              boxShadow: `0 0 6px 2px ${visual.glow}`,
              flexShrink: 0,
            }} />

            {/* Label */}
            <span style={{ fontSize: 10, fontWeight: 700, color: visual.color, letterSpacing: '0.07em' }}>
              {visual.label}
            </span>

            {/* Divider */}
            <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />

            {/* Desktop orb toggle */}
            <span style={{ fontSize: 9, color: '#888', whiteSpace: 'nowrap' as const }}>Desktop Orb</span>
            <button
              id="sovereign-desktop-orb-toggle"
              onClick={toggleDesktopOrb}
              title={orbDesktopOn ? 'Hide desktop orb' : 'Show desktop orb'}
              style={{
                width: 32, height: 18, borderRadius: 9, border: 'none',
                background: orbDesktopOn ? visual.color : '#374151',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.25s ease',
                flexShrink: 0,
                padding: 0,
              }}
            >
              <div style={{
                position: 'absolute',
                top: 2, left: orbDesktopOn ? 16 : 2,
                width: 14, height: 14, borderRadius: '50%',
                background: 'white',
                transition: 'left 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
              }} />
            </button>

            {/* Kill session */}
            <button
              onClick={killSession}
              title="End session"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#ef4444', fontSize: 12, padding: '0 2px',
                lineHeight: 1, opacity: 0.7,
              }}
            >⬡</button>
          </div>
        </div>
      ) : (

      /* ── Normal in-browser Orb (no agent) ──────────────────────────── */
      <div
        style={{
          position: 'fixed',
          left: position.x - halfOrb,
          top: position.y - halfOrb,
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          pointerEvents: 'auto',
        }}
      >
        {/* ── Expanded tray (above orb) ─────────────────────────────── */}
        {expanded && (
          <div
            className="orb-tray"
            style={{
              width: 300,
              background: pillBg,
              border: `1px solid ${visual.color}44`,
              borderRadius: 16,
              padding: '14px 16px',
              color: '#fff',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 12,
              marginBottom: 8,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: visual.color }}>
                Sovereign Orb
              </div>
              <div style={{
                fontSize: 10, fontWeight: 600, padding: '2px 8px',
                borderRadius: 6, background: `${visual.color}22`, color: visual.color,
              }}>
                {visual.label}
              </div>
            </div>

            {/* Agent status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 11, color: '#aaa' }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: config.agentConnected ? '#10b981' : '#6b7280' }} />
              {config.agentConnected ? 'Agent connected' : 'Agent not connected'}
            </div>

            {/* ── Connect Agent Section ─────────────────────────────────── */}
            {!config.agentConnected && !config.sessionToken && (
              <div style={{ marginBottom: 10 }}>
                {!showConnectPanel ? (
                  <button
                    id="sovereign-connect-agent-btn"
                    className="connect-agent-btn"
                    onClick={() => setShowConnectPanel(true)}
                    style={{
                      width: '100%',
                      padding: '10px 0',
                      borderRadius: 10,
                      border: 'none',
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      letterSpacing: '0.03em',
                      boxShadow: '0 2px 10px rgba(99,102,241,0.3)',
                    }}
                  >
                    🖥 Connect Desktop Agent
                  </button>
                ) : (
                  <div style={{
                    background: 'rgba(99,102,241,0.08)',
                    border: '1px solid rgba(99,102,241,0.2)',
                    borderRadius: 12,
                    padding: 12,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#a5b4fc', marginBottom: 6 }}>
                      🔗 Connect Your Agent
                    </div>
                    <p style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1.5, margin: '0 0 10px 0' }}>
                      Generate a session token to connect the desktop agent.
                      Your Replica will have full computer access, governed by the Decision Guardian™.
                    </p>

                    {connectError && (
                      <div style={{
                        fontSize: 10, color: '#ef4444', marginBottom: 8,
                        background: 'rgba(239,68,68,0.1)', borderRadius: 6,
                        padding: '4px 8px',
                      }}>
                        {connectError}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        id="sovereign-generate-token-btn"
                        onClick={handleConnectAgent}
                        disabled={connectLoading}
                        style={{
                          flex: 1,
                          padding: '8px 0',
                          borderRadius: 8,
                          border: 'none',
                          background: connectLoading
                            ? 'linear-gradient(90deg, #4f46e5, #7c3aed, #4f46e5)'
                            : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          backgroundSize: connectLoading ? '200% 100%' : 'auto',
                          animation: connectLoading ? 'shimmer 1.5s linear infinite' : 'none',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: 11,
                          cursor: connectLoading ? 'wait' : 'pointer',
                          fontFamily: 'inherit',
                          opacity: connectLoading ? 0.8 : 1,
                        }}
                      >
                        {connectLoading ? '⟳ Generating…' : '⚡ Generate Token'}
                      </button>
                      <button
                        onClick={() => { setShowConnectPanel(false); setConnectError(null); }}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.1)',
                          background: 'transparent',
                          color: '#9ca3af',
                          fontSize: 11,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Token Generated — Show Download + Connect Instructions ── */}
            {config.sessionToken && !config.agentConnected && generatedCmd && (
              <div style={{
                background: 'rgba(16,185,129,0.06)',
                border: '1px solid rgba(16,185,129,0.2)',
                borderRadius: 12,
                padding: 12,
                marginBottom: 10,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#34d399', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 6, height: 6, borderRadius: '50%', background: '#34d399',
                    animation: 'orbPulse 2s ease-in-out infinite',
                    boxShadow: '0 0 6px #34d399',
                  }} />
                  Session Active — Waiting for Agent
                </div>

                {/* Step 1: Download Agent (first time) */}
                <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                  Step 1: Download Agent (one-time)
                </div>
                <a
                  href={AGENT_DOWNLOAD_URLS[userPlatform] || AGENT_DOWNLOAD_URLS.other}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    textAlign: 'center' as const,
                    padding: '8px 0',
                    borderRadius: 8,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 11,
                    textDecoration: 'none',
                    marginBottom: 8,
                    fontFamily: 'inherit',
                  }}
                >
                  {userPlatform === 'windows' ? '⬇ Download for Windows (.exe)'
                    : userPlatform === 'macos' ? '⬇ Download for macOS (.dmg)'
                    : '⬇ Download Agent'}
                </a>

                {/* Step 2: Connect token */}
                <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                  Step 2: Connect
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  {/* Option A: Deep link (if agent already installed) */}
                  <button
                    id="sovereign-deeplink-btn"
                    onClick={launchDeepLink}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 8,
                      border: 'none',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 10,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    🔗 Launch Agent
                  </button>
                  {/* Option B: Download .hrconnect file */}
                  <button
                    id="sovereign-hrconnect-btn"
                    onClick={downloadHrConnect}
                    style={{
                      flex: 1,
                      padding: '8px 0',
                      borderRadius: 8,
                      border: '1px solid rgba(99,102,241,0.3)',
                      background: copied ? 'rgba(16,185,129,0.2)' : 'rgba(99,102,241,0.15)',
                      color: copied ? '#34d399' : '#a5b4fc',
                      fontWeight: 700,
                      fontSize: 10,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {copied ? '✓ Downloaded' : '📄 Get Token File'}
                  </button>
                </div>

                <div style={{ fontSize: 9, color: '#6b7280', textAlign: 'center' as const, lineHeight: 1.5 }}>
                  <strong style={{ color: '#9ca3af' }}>Launch Agent</strong> opens the installed agent automatically.<br />
                  <strong style={{ color: '#9ca3af' }}>Get Token File</strong> downloads a <code style={{ color: '#a5b4fc' }}>.hrconnect</code> file — the agent picks it up from your Downloads folder.
                </div>
              </div>
            )}

            {/* ── Hardware status badge ─────────────────────────────────── */}
            {hwStatus ? (
              <div style={{
                border: '1px solid rgba(16,185,129,0.25)',
                borderRadius: 10,
                padding: '8px 10px',
                marginBottom: 10,
                background: 'rgba(16,185,129,0.06)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                  <span style={{ fontSize: 13 }}>{hwStatus.backend === 'GPU' ? '🏢' : '🖥️'}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#10b981' }}>Sovereign Engine</span>
                  <span style={{ fontSize: 8, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.2)', color: '#10b981' }}>LIVE</span>
                </div>
                <div style={{ fontSize: 10, color: '#ddd', marginBottom: 4, paddingLeft: 19 }}>
                  {hwStatus.model.split('(')[0].trim()}
                </div>
                <div style={{ display: 'flex', gap: 4, paddingLeft: 19, flexWrap: 'wrap' as const }}>
                  <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 4, background: 'rgba(99,102,241,0.2)', color: '#818cf8', fontWeight: 600 }}>{hwStatus.tier.toUpperCase()}</span>
                  <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 600 }}>
                    {hwStatus.backend}{hwStatus.vram_gb ? ` · ${hwStatus.vram_gb}GB VRAM` : hwStatus.ram_gb ? ` · ${hwStatus.ram_gb}GB RAM` : ''}
                  </span>
                  <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.15)', color: '#10b981', fontWeight: 600 }}>🔒 Zero Cloud</span>
                </div>
              </div>
            ) : null}

            {/* Recent actions */}
            {recentActions.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#666', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Recent
                </div>
                {recentActions.map((a, i) => (
                  <div key={i} className="action-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 4px', borderRadius: 6, fontSize: 11 }}>
                    <span style={{ color: '#ddd' }}>{a.action.replace('computer.', '')}</span>
                    <span style={{ color: a.ok ? '#10b981' : '#ef4444' }}>{a.ok ? '✓' : '✗'} {a.ts}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Kill switch (only if session active) */}
            {config.sessionToken && (
              <button
                onClick={killSession}
                style={{
                  width: '100%', padding: '8px 0', borderRadius: 10, border: 'none',
                  background: 'rgba(239,68,68,0.15)', color: '#ef4444',
                  fontWeight: 700, fontSize: 12, cursor: 'pointer', marginTop: 2,
                  fontFamily: 'inherit',
                }}
              >
                ⬡ End Session
              </button>
            )}
          </div>
        )}

        {/* ── The Orb ───────────────────────────────────────────────── */}
        <div
          className={`sovereign-orb ${visual.pulse ? 'orb-pulse' : ''}`}
          onMouseDown={onMouseDown}
          onClick={() => !dragging && setExpanded(e => !e)}
          style={{
            '--orb-glow': visual.glow,
            width: orbSize,
            height: orbSize,
            borderRadius: '50%',
            background: `radial-gradient(circle at 35% 35%, ${visual.color}ee, ${visual.color}88)`,
            boxShadow: `0 0 22px 6px ${visual.glow}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'visible',
          } as React.CSSProperties}
        >
          {orbState === 'acting' && (
            <div style={{
              position: 'absolute', width: orbSize + 12, height: orbSize + 12,
              borderRadius: '50%',
              border: `2px solid ${visual.color}88`,
              borderTopColor: visual.color,
              animation: 'orbSpin 0.8s linear infinite',
              pointerEvents: 'none',
            }} />
          )}
          <div style={{ fontSize: 22, lineHeight: 1 }}>
            {orbState === 'killed'           && '⬡'}
            {orbState === 'pending_approval' && '⏳'}
            {orbState === 'copilot'          && '🧭'}
            {(orbState === 'idle' || orbState === 'capturing' || orbState === 'acting') && '✦'}
          </div>
        </div>

        {/* State label */}
        <div style={{
          fontSize: 9, fontWeight: 600, color: visual.color,
          fontFamily: 'Inter, system-ui, sans-serif',
          textAlign: 'center', letterSpacing: '0.05em',
          pointerEvents: 'none',
          textShadow: `0 0 8px ${visual.glow}`,
        }}>
          {visual.label.split('·')[0].trim().toUpperCase()}
        </div>
      </div>
      )}
    </>
  );
};

export default SovereignOrb;
